// Library Methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";
import ExamCardToggles from "../card-ui/ExamCardToggles/ExamCardToggles";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_ANISEIKONIA_CARD,
  aniseikoniaTargetStyleOptions,
} from "src/constants/enums";

type AniseikoniaCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const AniseikoniaCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: AniseikoniaCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_ANISEIKONIA_CARD;

  const {
    horizontal,
    vertical,
    overall,
    optionalToggles,
    aniseikoniaTargetStyle,
  } = examCardInfo;

  const selectedTargetStyle =
    aniseikoniaTargetStyle ?? aniseikoniaTargetStyleOptions[0];

  const targetStyleOptions = useMemo(
    () =>
      aniseikoniaTargetStyleOptions.map((option) => ({
        label: t(option),
        value: option,
      })),
    [t]
  );

  const meridianToggles = useMemo(
    () => [
      {
        key: "horizontal" as const,
        title: t("Horizontal"),
        value: Boolean(horizontal),
      },
      {
        key: "vertical" as const,
        title: t("Vertical"),
        value: Boolean(vertical),
      },
      {
        key: "overall" as const,
        title: t("Overall"),
        value: Boolean(overall),
      },
    ],
    [t, horizontal, vertical, overall]
  );

  // At least one meridian has to be measured, so turning the last one off
  // switches Horizontal on instead of leaving the section with none
  const updateMeridian = useCallback(
    (key: (typeof meridianToggles)[number]["key"], value: boolean) => {
      const isLastOneOn =
        !value &&
        !meridianToggles.some(
          (meridian) => meridian.key !== key && meridian.value
        );

      if (!isLastOneOn) {
        updateExamCardProperty(key, value);
        return;
      }

      if (key !== "horizontal") updateExamCardProperty(key, false);
      updateExamCardProperty("horizontal", true);
    },
    [meridianToggles, updateExamCardProperty]
  );

  const booleanOptions = useMemo(
    () => [
      { value: true, label: t("On") },
      { value: false, label: t("Off") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  const titleStyle = {
    ...textStyle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  };

  // A button only needs to wrap to a new line once its own label can't fit -- not at some
  // arbitrary shared width. "ch" scales with character count, so each button's floor tracks its
  // own text; +2ch covers the button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
            <Grid item display="flex" flexDirection="column" gap="4px">
              <TitleWithTooltip
                title={t("Target Style")}
                tooltip={t(
                  "Controls the shape the patient compares the two halves of."
                )}
                titleStyle={titleStyle}
                tooltipStyle={textStyle}
              />
              <Grid container item display="flex" flexWrap="wrap" gap="4px">
                {targetStyleOptions.map((option) => (
                  <Grid
                    item
                    xs
                    fontSize="12px"
                    minWidth={buttonMinWidth(option.label)}
                    key={option.value}
                  >
                    <ExamCardButton
                      isSelected={selectedTargetStyle === option.value}
                      updateValue={() =>
                        updateExamCardProperty(
                          "aniseikoniaTargetStyle",
                          option.value
                        )
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              border: "1px solid #5B6D7A",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            <Box
              component="span"
              sx={{ ...textStyle, fontWeight: 600 }}
              data-testid="aniseikonia-directions-group-title"
            >
              {t("Directions to Measure")}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "8px",
              }}
            >
              {meridianToggles.map((meridian) => (
                <Grid
                  item
                  display="flex"
                  flexDirection="column"
                  gap="4px"
                  key={meridian.key}
                >
                  <TitleWithTooltip
                    title={meridian.title}
                    tooltip={t(
                      "Controls which meridians the patient is asked to match. At least one is required."
                    )}
                    titleStyle={titleStyle}
                    tooltipStyle={textStyle}
                  />

                  <Grid container item display="flex" flexWrap="wrap" gap="4px">
                    {booleanOptions.map((option) => (
                      <Grid
                        item
                        xs
                        fontSize="12px"
                        minWidth={buttonMinWidth(option.label)}
                        key={String(option.value)}
                      >
                        <ExamCardButton
                          isSelected={
                            option.value ? meridian.value : !meridian.value
                          }
                          updateValue={() =>
                            updateMeridian(meridian.key, option.value)
                          }
                          label={option.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          <ExamCardToggles
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={DEFAULT_ANISEIKONIA_CARD.optionalToggles}
          />
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default AniseikoniaCard;
